import type { VoidComponent } from "solid-js";
import { createEffect, createMemo, createSignal } from "solid-js";
import { IconButton } from "../../../components/button/Button";
import { useTranslation } from "../../../state/i18n";
import VersionHistoryShowEvent from "../../../events/VersionHistoryShowEvent";
import UserService from "../../../services/UserService.ts";
import ApiService from "../../../services/ApiService.ts";
import { useCustomerState } from "../../../state/customer.tsx";
import { useFormState } from "../../../state/form.tsx";
import { useModalManager } from "../../../components/modal/ModalManager";
import GenericFailureModal from "../../GenericFailureModal";
import { isNil } from "lodash-es";
import FormVersionService from "../../../services/FormVersionService.ts";

const HeaderVersionHistoryButton: VoidComponent = () => {
  const { t } = useTranslation();
  const customer = useCustomerState();
  const form = useFormState();
  const { open } = useModalManager();

  // Fetch queries on page load so they are cached for when the drawer is opened
  const apiKeysQuery = ApiService.getApiKeys();
  const usersQuery = UserService.getUsers(customer.customerId);
  const formVersionsQuery = ApiService.getFormVersionsInfinite(form.id);
  const isLoading = createMemo(() => apiKeysQuery.isFetching || usersQuery.isFetching || formVersionsQuery.isFetching);

  const error = createMemo(() => apiKeysQuery.error ?? usersQuery.error ?? formVersionsQuery.error);

  const [openPending, setOpenPending] = createSignal<boolean>(false);

  const openVersionHistoryDrawer = () => new VersionHistoryShowEvent().send();

  const openHistory = () => {
    const currentError = error();
    if (!isNil(currentError)) {
      open({
        content: () => (
          <GenericFailureModal title={t(($) => $.VERSION_HISTORY_OPEN_ERROR_MODAL_TITLE)} error={currentError} />
        ),
      });
      return;
    }
    openVersionHistoryDrawer();
  };

  createEffect(() => {
    if (!isLoading() && openPending()) {
      setOpenPending(false);
      openHistory();
    }
  });

  return (
    <IconButton
      icon="versionHistory"
      iconType="outline"
      loading={openPending()}
      aria-label={t(($) => $.FORM_EDITOR_HEADER_HISTORY_BUTTON)}
      variant="secondary"
      disabled={isNil(form.publishedVersion) && !FormVersionService.hasUpdated()}
      onClick={() => {
        if (isLoading()) {
          setOpenPending(true);
          return;
        }
        openHistory();
      }}
    />
  );
};

export default HeaderVersionHistoryButton;
